import { useState } from 'react'
import { ShieldCheck, AlertTriangle, XCircle, BookOpen, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import api from '../lib/api'

// ── Small stat badge ──────────────────────────────────────────────────────────

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>{label}</div>
    </div>
  )
}

// ── Collapsible section ───────────────────────────────────────────────────────

function Section({ title, icon: Icon, color, items, renderItem, emptyMsg }) {
  const [open, setOpen] = useState(true)
  if (!items.length) return null
  return (
    <div style={{ marginBottom: '1rem' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '0.5rem 0', background: 'none', border: 'none',
          cursor: 'pointer', color,
        }}
      >
        <Icon size={14} />
        <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1, textAlign: 'left' }}>
          {title} ({items.length})
        </span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.map((item, i) => renderItem(item, i))}
        </div>
      )}
    </div>
  )
}

// ── Citation row ──────────────────────────────────────────────────────────────

function CitRow({ cit, accent }) {
  return (
    <div style={{
      padding: '0.5rem 0.75rem',
      background: '#f8fafc',
      borderLeft: `3px solid ${accent}`,
      borderRadius: '0 4px 4px 0',
      fontSize: '0.8rem',
    }}>
      <div style={{ fontWeight: 600, color: '#1e293b' }}>
        {cit.author} ({cit.year})
      </div>
      {cit.reference && (
        <div style={{ color: '#475569', marginTop: 2, fontStyle: 'italic', fontSize: '0.75rem' }}>
          {cit.reference.apa_formatted}
        </div>
      )}
      {cit.reason && (
        <div style={{ color: '#dc2626', marginTop: 2, fontSize: '0.72rem' }}>⚠ {cit.reason}</div>
      )}
      {cit.chapters?.length > 0 && (
        <div style={{ color: '#94a3b8', marginTop: 2, fontSize: '0.7rem' }}>
          Ch. {cit.chapters.map(c => c.number).join(', ')}
        </div>
      )}
    </div>
  )
}

// ── Unused reference row ──────────────────────────────────────────────────────

function RefRow({ ref }) {
  return (
    <div style={{
      padding: '0.5rem 0.75rem',
      background: '#f8fafc',
      borderLeft: '3px solid #94a3b8',
      borderRadius: '0 4px 4px 0',
      fontSize: '0.75rem',
      color: '#475569',
      fontStyle: 'italic',
    }}>
      {ref.apa_formatted}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CitationAuditPanel({ thesisId }) {
  const [state, setState] = useState('idle') // idle | loading | done | error
  const [report, setReport] = useState(null)

  async function runAudit() {
    setState('loading')
    try {
      const { data } = await api.get(`/thesis/${thesisId}/citation-audit`)
      setReport(data)
      setState('done')
    } catch (err) {
      console.error('Citation audit failed:', err)
      setState('error')
    }
  }

  if (state === 'idle') {
    return (
      <button
        onClick={runAudit}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '0.6rem 1rem',
          background: '#1e3a5f', color: '#93c5fd',
          border: '1px solid rgba(147,197,253,0.3)', borderRadius: 8,
          fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', width: '100%',
          justifyContent: 'center',
        }}
      >
        <ShieldCheck size={14} />
        Run Citation Audit
      </button>
    )
  }

  if (state === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem', color: '#64748b', fontSize: '0.8rem' }}>
        <Loader2 size={14} className="animate-spin" />
        Scanning chapters…
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div style={{ padding: '0.75rem', color: '#dc2626', fontSize: '0.8rem' }}>
        Audit failed. Check the console.
        <button onClick={() => setState('idle')} style={{ marginLeft: 8, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}>
          Retry
        </button>
      </div>
    )
  }

  const { summary, matched, unmatched, suspicious, unusedRefs } = report

  return (
    <div style={{ fontSize: '0.8rem' }}>
      {/* Summary bar */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.5rem', padding: '0.75rem',
        background: '#f0f9ff', borderRadius: 8,
        marginBottom: '1rem', border: '1px solid #bae6fd',
      }}>
        <Stat label="Matched"    value={summary.matched}    color="#16a34a" />
        <Stat label="Unmatched"  value={summary.unmatched}  color="#ea580c" />
        <Stat label="Suspicious" value={summary.suspicious} color="#dc2626" />
        <Stat label="Unused"     value={summary.unusedRefs} color="#94a3b8" />
      </div>

      <p style={{ color: '#64748b', fontSize: '0.7rem', marginBottom: '0.75rem' }}>
        {summary.totalCitations} unique citations across {summary.chaptersScanned} chapter{summary.chaptersScanned !== 1 ? 's' : ''}
      </p>

      {/* Suspicious — show first */}
      <Section
        title="Suspicious (possible hallucination)"
        icon={XCircle}
        color="#dc2626"
        items={suspicious}
        renderItem={(cit, i) => <CitRow key={i} cit={cit} accent="#dc2626" />}
      />

      {/* Unmatched — in text but not in reference list */}
      <Section
        title="Not in reference list"
        icon={AlertTriangle}
        color="#ea580c"
        items={unmatched}
        renderItem={(cit, i) => <CitRow key={i} cit={cit} accent="#ea580c" />}
      />

      {/* Matched */}
      <Section
        title="Verified citations"
        icon={ShieldCheck}
        color="#16a34a"
        items={matched}
        renderItem={(cit, i) => <CitRow key={i} cit={cit} accent="#16a34a" />}
      />

      {/* Unused references */}
      <Section
        title="References not cited"
        icon={BookOpen}
        color="#94a3b8"
        items={unusedRefs}
        renderItem={(ref, i) => <RefRow key={i} ref={ref} />}
      />

      {summary.matched === summary.totalCitations && summary.suspicious === 0 && (
        <div style={{ padding: '0.75rem', background: '#f0fdf4', borderRadius: 6, color: '#16a34a', fontSize: '0.8rem', textAlign: 'center', border: '1px solid #bbf7d0' }}>
          All citations verified ✓
        </div>
      )}

      <button
        onClick={() => { setState('idle'); setReport(null) }}
        style={{ marginTop: '0.75rem', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
      >
        Clear
      </button>
    </div>
  )
}
